import { TelegramHelpButton } from '../components/shared/TelegramHelpButton';
import { useFieldErrors } from '../hooks/useFieldErrors';
import { useState } from 'react';
import { useResetOnOpen } from '../hooks/useResetOnOpen';
import {
  Text, Spinner, Badge, Button, Field, Input, Dropdown, Option,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
  makeStyles, tokens, Tooltip,
} from '@fluentui/react-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchCustomers, fetchSaleOrdersByRancho, createSaleOrder,
  type Customer, type SaleOrderItem, type CreateSaleOrderInput,
} from '../api/sales.api';
import { KpiCard } from '../components/shared/KpiCard';
import { ChartCard } from '../components/shared/ChartCard';
import { DataTable, type DataTableColumn } from '../components/shared/DataTable';
import { BarLineChart } from '../components/shared/charts/BarLineChart';
import { HorizontalBarList, type HorizontalBarPoint } from '../components/shared/charts/HorizontalBarList';
import { useT } from '../i18n';
import { NewCustomerDialog } from '../components/sales/NewCustomerDialog';
import { NumberInput } from '../components/shared/NumberInput';
import { useIntlFormat } from '../hooks/useIntlFormat';
import { usePermission } from '../hooks/usePermission';
import { useRanchoStore } from '../store/rancho.store';
import { useRanchoLotes } from '../hooks/useRanchoLotes';
import { cropLabel } from '../utils/crop-label';
import { PageTitle } from '../components/shared/PageTitle';

const useStyles = makeStyles({
  root:   { display: 'flex', flexDirection: 'column', gap: '24px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' },
  kpiRow: { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  splitRow: { display: 'flex', gap: tokens.spacingHorizontalL, flexWrap: 'wrap', alignItems: 'flex-start' },
  splitCol: { flex: '1', minWidth: '320px' },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius:    tokens.borderRadiusMedium,
    padding:         '20px',
    display:         'flex',
    flexDirection:   'column',
    gap:             '12px',
    overflowX:       'auto',
  },
  positive: { color: tokens.colorPaletteGreenForeground1, fontVariantNumeric: 'tabular-nums' },
  negative: { color: tokens.colorPaletteRedForeground1,   fontVariantNumeric: 'tabular-nums' },
  formGrid: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' },
});

const STATUS_KEYS: Record<string, string> = {
  DRAFT:     'statusDraft',
  CONFIRMED: 'statusConfirmed',
  SHIPPED:   'statusFulfilled',
  INVOICED:  'statusFulfilled',
  CANCELLED: 'statusCancelled',
};

function variance(item: SaleOrderItem): number {
  return item.unitPrice - (item.targetPrice ?? item.unitPrice);
}

function NewSaleOrderDialog({ open, onClose, customers, onStockNotice }: { open: boolean; onClose: () => void; customers: Customer[]; onStockNotice: (msg: string) => void }) {
  const { lotes } = useRanchoLotes(open);
  const canCreateCustomer = usePermission('sales:write');
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const s  = useStyles();
  const t  = useT();
  const s2 = t.sales;
  const qc = useQueryClient();

  const [customerId, setCustomerId] = useState('');
  const [loteId, setLoteId] = useState('');
  const [cropName, setCropName] = useState('');
  const [qty, setQty] = useState('');
  const [targetPrice, setTargetPrice] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [error, setError] = useState('');
  const fe = useFieldErrors();

  const reset = () => {
    fe.clear();
    setCustomerId(''); setLoteId(''); setCropName(''); setQty(''); setTargetPrice(''); setUnitPrice(''); setError('');
  };

  useResetOnOpen(open, reset);

  const mut = useMutation({
    mutationFn: () => {
      const input: CreateSaleOrderInput = {
        customerId,
        currency: 'MXN',
        orderDate: new Date().toISOString().slice(0, 10),
        items: [{
          cropName: cropName.trim(),
          qty: Number(qty),
          unit: 'kg',
          unitPrice: Number(unitPrice),
          targetPrice: targetPrice ? Number(targetPrice) : undefined,
        }],
      };
      return createSaleOrder({ ...input, loteId: loteId || undefined });
    },
    onSuccess: (order) => {
      const w = order.stockWarning;
      onStockNotice(w ? s2.stockNotice(w.overByKg, w.loteId) : '');
      qc.invalidateQueries({ queryKey: ['saleOrders'] });
      reset();
      onClose();
    },
    onError: (e: any) => setError(e?.response?.data?.error ?? s2.createError),
  });

  const handleSubmit = () => {
    setError('');
    const req = t.common.validation.fieldRequired;
    const ok = fe.validate({
      customerId: !customerId && req,
      cropName: !cropName.trim() && req,
      qty: !qty && req,
      unitPrice: !unitPrice && req,
    });
    if (!ok) return;
    mut.mutate();
  };

  return (
    <>
    <NewCustomerDialog
      open={customerDialogOpen}
      onClose={() => setCustomerDialogOpen(false)}
      onCreated={(c) => setCustomerId(c.customerId)}
    />
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle action={<TelegramHelpButton topic="sales" voiceExample={t.telegramHelp.examples.sales[0].voice} voiceDesc={t.telegramHelp.examples.sales[0].desc} />}>{s2.newOrder}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Field label={s2.customer} required {...fe.props('customerId')}>
                <Dropdown
                  value={customers.find(c => c.customerId === customerId)?.name ?? ''}
                  placeholder={customers.length === 0 ? s2.noCustomers : undefined}
                  onOptionSelect={(_, d) => { setCustomerId(d.optionValue ?? ''); fe.clear('customerId'); }}
                >
                  {customers.map(c => (
                    <Option key={c.customerId} value={c.customerId}>{c.name}</Option>
                  ))}
                </Dropdown>
              </Field>
              {customers.length === 0 && <Text size={200}>{s2.noCustomers}</Text>}
              {canCreateCustomer && (
                <Button appearance="transparent" size="small" style={{ alignSelf: 'flex-start' }} onClick={() => setCustomerDialogOpen(true)}>
                  {s2.newCustomer}
                </Button>
              )}
              <Field label={s2.lote}>
                <Dropdown
                  value={lotes.find(l => l.loteId === loteId)?.name ?? ''}
                  selectedOptions={loteId ? [loteId] : []}
                  onOptionSelect={(_, d) => setLoteId(d.optionValue ?? '')}
                >
                  <Option value="">{s2.loteOptional}</Option>
                  {lotes.map((l) => <Option key={l.loteId} value={l.loteId}>{l.name}</Option>)}
                </Dropdown>
              </Field>
              <Field label={s2.crop} required {...fe.props('cropName')}>
                <Input value={cropName} onChange={(_, d) => { setCropName(d.value); fe.clear('cropName'); }} />
              </Field>
              <div className={s.formGrid}>
                <Field label={s2.qty} required {...fe.props('qty')}>
                  <NumberInput value={qty} onValueChange={(v) => { setQty(v); fe.clear('qty'); }} />
                </Field>
                <Field label={s2.targetPrice}>
                  <NumberInput value={targetPrice} onValueChange={(v) => setTargetPrice(v)} />
                </Field>
                <Field label={s2.unitPrice} required {...fe.props('unitPrice')}>
                  <NumberInput value={unitPrice} onValueChange={(v) => { setUnitPrice(v); fe.clear('unitPrice'); }} />
                </Field>
              </div>
              {error && <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>{t.common.cancel}</Button>
            <Button appearance="primary" onClick={handleSubmit} disabled={mut.isPending}>
              {mut.isPending ? <Spinner size="tiny" /> : t.common.save}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
    </>
  );
}

export function SalesPage() {
  const s = useStyles();
  const t = useT();
  const { fmtNum } = useIntlFormat();
  const fmt2 = (v: number) => fmtNum(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const s2 = t.sales;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const canCreate = usePermission('sales:write');

  const activeRanchoId = useRanchoStore(st => st.activeRanchoId);
  const { data: customers = [] } = useQuery({ queryKey: ['customers'], queryFn: fetchCustomers });
  const [stockNotice, setStockNotice] = useState('');
  const { data: orders = [], isLoading } = useQuery({ queryKey: ['saleOrders', activeRanchoId], queryFn: () => fetchSaleOrdersByRancho(activeRanchoId) });

  const customerName = (customerId: string) => customers.find(c => c.customerId === customerId)?.name ?? customerId;

  const rows = orders.flatMap(order =>
    order.items.map((item, idx) => ({
      key: `${order.saleOrderId}-${idx}`,
      order,
      item,
    })),
  );

  // ── KPIs y agregaciones — client-side sobre orders/rows ya cargados, sin endpoint nuevo. ──
  const kpiRevenue = orders.reduce((sum, o) => sum + o.totalAmount, 0);
  const totalQty = rows.reduce((sum, r) => sum + r.item.qty, 0);
  const kpiAvgPrice = totalQty > 0
    ? rows.reduce((sum, r) => sum + r.item.unitPrice * r.item.qty, 0) / totalQty
    : 0;
  const rowsWithTarget = rows.filter(r => r.item.targetPrice != null);
  const kpiAvgVariance = rowsWithTarget.length
    ? rowsWithTarget.reduce((sum, r) => sum + variance(r.item), 0) / rowsWithTarget.length
    : 0;

  function topBy<K extends string>(keyOf: (r: (typeof rows)[number]) => K, valueOf: (r: (typeof rows)[number]) => number): K | null {
    const totals = new Map<K, number>();
    for (const r of rows) totals.set(keyOf(r), (totals.get(keyOf(r)) ?? 0) + valueOf(r));
    let best: K | null = null; let bestVal = -Infinity;
    for (const [k, v] of totals) if (v > bestVal) { best = k; bestVal = v; }
    return best;
  }
  const topCustomerId = topBy(r => r.order.customerId, r => r.item.unitPrice * r.item.qty);
  const topCrop = topBy(r => r.item.cropName, r => r.item.qty);

  // Precio histórico — promedio mensual ponderado por cantidad, observado en órdenes reales
  // (no es un histórico multi-año, no hay ese dato en el sistema).
  const monthlyPrice = new Map<string, { sum: number; qty: number }>();
  for (const r of rows) {
    const month = r.order.orderDate.slice(0, 7);
    const acc = monthlyPrice.get(month) ?? { sum: 0, qty: 0 };
    acc.sum += r.item.unitPrice * r.item.qty;
    acc.qty += r.item.qty;
    monthlyPrice.set(month, acc);
  }
  const monthlyAvgs = [...monthlyPrice.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, acc]) => ({ month, avg: acc.qty > 0 ? acc.sum / acc.qty : 0 }));
  const maxMonthlyAvg = Math.max(1, ...monthlyAvgs.map(m => m.avg));
  const priceHistoryData = monthlyAvgs.map(m => ({ label: m.month.slice(5), barHeightPct: (m.avg / maxMonthlyAvg) * 100 }));

  // Rango de precio proyectado por cultivo — contrato (promedio de targetPrice) y rango real observado.
  const byCrop = new Map<string, { targets: number[]; prices: number[] }>();
  for (const r of rows) {
    const acc = byCrop.get(r.item.cropName) ?? { targets: [], prices: [] };
    if (r.item.targetPrice != null) acc.targets.push(r.item.targetPrice);
    acc.prices.push(r.item.unitPrice);
    byCrop.set(r.item.cropName, acc);
  }
  const priceRangeRows = [...byCrop.entries()].map(([cropName, acc]) => ({
    cropName: cropLabel(cropName),
    contract: acc.targets.length ? acc.targets.reduce((a, b) => a + b, 0) / acc.targets.length : null,
    min: Math.min(...acc.prices),
    max: Math.max(...acc.prices),
  }));

  // Desempeño por cliente — revenue total por customerId.
  const revenueByCustomer = new Map<string, number>();
  for (const o of orders) revenueByCustomer.set(o.customerId, (revenueByCustomer.get(o.customerId) ?? 0) + o.totalAmount);
  const maxCustomerRevenue = Math.max(1, ...revenueByCustomer.values());
  const clientPerformanceData: HorizontalBarPoint[] = [...revenueByCustomer.entries()]
    .sort(([, a], [, b]) => b - a)
    .map(([customerId, revenue]) => ({
      label: customerName(customerId),
      displayValue: fmtNum(revenue, { maximumFractionDigits: 0 }),
      widthPct: (revenue / maxCustomerRevenue) * 100,
    }));

  const priceRangeColumns: DataTableColumn<(typeof priceRangeRows)[number]>[] = [
    { key: 'cropName', header: s2.crop, width: 1.2 },
    { key: 'contract', header: s2.targetPrice, align: 'right', render: r => r.contract != null ? fmt2(r.contract) : '—' },
    { key: 'range', header: s2.priceRange, align: 'right', render: r => `${fmt2(r.min)} – ${fmt2(r.max)}` },
  ];

  return (
    <div className={s.root}>
      <div className={s.header}>
        <PageTitle>{s2.title}</PageTitle>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {canCreate ? (
            <Button appearance="secondary" onClick={() => setCustomerDialogOpen(true)}>{s2.newCustomer}</Button>
          ) : (
            <Tooltip content={s2.newCustomerNoPermission} relationship="description">
              <Button appearance="secondary" disabled>{s2.newCustomer}</Button>
            </Tooltip>
          )}
          {canCreate ? (
            <Button appearance="primary" onClick={() => setDialogOpen(true)}>{s2.newOrder}</Button>
          ) : (
            <Tooltip content={s2.newOrderNoPermission} relationship="description">
              <Button appearance="primary" disabled>{s2.newOrder}</Button>
            </Tooltip>
          )}
        </div>
      </div>

      {!isLoading && orders.length > 0 && (
        <>
          <div className={s.kpiRow}>
            <KpiCard label={s2.kpiRevenue} value={fmtNum(kpiRevenue, { maximumFractionDigits: 0 })} />
            <KpiCard label={s2.kpiOrders} value={orders.length} />
            <KpiCard label={s2.kpiAvgPrice} value={fmt2(kpiAvgPrice)} />
            <KpiCard label={s2.kpiAvgVariance} value={fmt2(kpiAvgVariance)} tone={kpiAvgVariance < 0 ? 'danger' : 'default'} />
            {topCustomerId && <KpiCard label={s2.kpiTopCustomer} value={customerName(topCustomerId)} />}
            {topCrop && <KpiCard label={s2.kpiTopCrop} value={cropLabel(topCrop)} />}
          </div>

          <div className={s.splitRow}>
            <div className={s.splitCol}>
              <ChartCard title={s2.priceHistoryTitle} subtitle={s2.priceHistorySubtitle} isEmpty={priceHistoryData.length < 2} emptyMessage={t.common.noData}>
                <BarLineChart data={priceHistoryData} />
              </ChartCard>
            </div>
            <div className={s.splitCol}>
              <ChartCard title={s2.priceRangeTitle}>
                <DataTable columns={priceRangeColumns} rows={priceRangeRows} rowKey={r => r.cropName} emptyMessage={t.common.noData} />
              </ChartCard>
            </div>
          </div>

          <ChartCard title={s2.performanceTitle}>
            <HorizontalBarList data={clientPerformanceData} labelWidth={140} />
          </ChartCard>
        </>
      )}

      <div className={s.card}>
        {isLoading ? (
          <Spinner label={t.common.loading} />
        ) : rows.length === 0 ? (
          <Text>{t.common.noData}</Text>
        ) : (
          <DataTable
            rows={rows}
            rowKey={(r) => r.key}
            columns={[
              { key: 'customer', header: s2.customer, render: ({ order }) => customerName(order.customerId) },
              { key: 'crop', header: s2.crop, render: ({ item }) => cropLabel(item.cropName) },
              { key: 'grade', header: s2.grade, render: ({ item }) => item.grade ?? '—' },
              { key: 'qty', header: s2.qty, align: 'right', render: ({ item }) => `${item.qty} ${item.unit}` },
              { key: 'target', header: s2.targetPrice, align: 'right', render: ({ item }) => item.targetPrice != null ? fmt2(item.targetPrice) : '—' },
              { key: 'unit', header: s2.unitPrice, align: 'right', render: ({ item }) => fmt2(item.unitPrice) },
              { key: 'variance', header: s2.variance, align: 'right', render: ({ item }) => {
                const v = variance(item);
                return <span className={v >= 0 ? s.positive : s.negative}>{v >= 0 ? '+' : ''}{fmt2(v)}</span>;
              } },
              { key: 'status', header: s2.status, render: ({ order }) => <Badge>{s2[STATUS_KEYS[order.status] as keyof typeof s2] ?? order.status}</Badge> },
              { key: 'total', header: s2.total, align: 'right', render: ({ order }) => `${fmt2(order.totalAmount)} ${order.currency}` },
            ]}
          />
        )}
      </div>

      {stockNotice && <Text role="status">{stockNotice}</Text>}
      <NewCustomerDialog open={customerDialogOpen} onClose={() => setCustomerDialogOpen(false)} />
      <NewSaleOrderDialog open={dialogOpen} onClose={() => setDialogOpen(false)} customers={customers} onStockNotice={setStockNotice} />
    </div>
  );
}
